import { getLocale } from "~/stores/lang";
import { DATE_CONST, SMALL_WIDTH_RESOLUTION_FOR_WEEKDAY, LENGHT_WEEKDAY_SUBSTRING } from "~/const";
import { ICalendarDayData, IDayItem, IWeeks } from "~/components/Calendar";

export function daysInMonth(date: Date): number {
	const month = date.getMonth() + 1;
	const year = date.getFullYear();

	return new Date(year, month, 0).getDate();
}

export function getFirstDay(date: Date): number {
	const firstDate = new Date(date);

	firstDate.setDate(1);
	const firstDay = firstDate.getDay();

	return firstDay === 0 ? DATE_CONST.sunday : firstDay - 1;
}

export function getShortcutOfWeekDay(weekDay: string, resolution: number): string {
	if (resolution < SMALL_WIDTH_RESOLUTION_FOR_WEEKDAY) {
		return weekDay.substring(0, LENGHT_WEEKDAY_SUBSTRING);
	}

	return weekDay;
}

// vraci predchozi mesic prvniho
export function getPrevMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth() - 1, 1);
}

// vraci pristi mesic prvniho
export function getNextMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

// vraci dalsi mesic a jeho posledni datum
export function getNextMonthEnd(date: Date): Date {
	/* eslint-disable no-magic-numbers */
	return new Date(date.getFullYear(), date.getMonth() + 2, 0);
}

// brezen 2022
export function getDateMonthTitle(date: Date, loc = getLocale()): string {
	return new Intl.DateTimeFormat(loc, { month: 'long', year: 'numeric' }).format(date);
}

// 4. brezna
export function getDateTitle(date: Date, loc = getLocale()): string {
	return new Intl.DateTimeFormat(loc, { month: 'long', day: 'numeric' }).format(date);
}

// patek
export function getDayOfWeek(date: Date, loc = getLocale()): string {
	return new Intl.DateTimeFormat(loc, { weekday: 'long' }).format(date);
}

// mesic cislem jen v cestine, v anglictine slovem ("20/10" by American cetl obracene) - zkratkou, nebo cely nazev (long)
function getMonthFormat(loc: string, long = false): "numeric" | "short" | "long" {
	if (loc.startsWith("cs")) {
		return "numeric";
	}

	return long ? "long" : "short";
}

// "31. 10. 2026" / "31 October 2026"
export function getDayTitle(date: Date, loc = getLocale()) {
	return new Intl.DateTimeFormat(loc, { day: "numeric", month: getMonthFormat(loc, true), year: "numeric" }).format(date);
}

// den a mesic bez roku: "20. 10." / "20 Oct", s withWeekday "úterý 20. 10." / "Tuesday 20 Oct"
export function getShortDateTitle(date: Date, withWeekday = false, loc = getLocale()) {
	return new Intl.DateTimeFormat(loc, {
		weekday: withWeekday ? "long" : undefined,
		day: "numeric",
		month: getMonthFormat(loc),
	}).format(date);
}

export function parseBeDate(date: string): Date {
	const parts = date.split("-").map(part => parseFloat(part));

	return new Date(parts[0], parts[1] - 1, parts[2]);
}

// "20. 10. 2026" / "20 Oct 2026"
export function beDateToLocal(beDate: string, loc = getLocale()): string {
	const date = beDate ? parseBeDate(beDate) : null;

	// prazdna nebo neplatna hodnota (Intl by na Invalid Date vyhodil vyjimku)
	return date && !isNaN(date.getTime())
		? new Intl.DateTimeFormat(loc, { day: "numeric", month: getMonthFormat(loc), year: "numeric" }).format(date)
		: beDate;
}

// "20. – 26. 10. 2026" / "20 – 26 Oct 2026", pres mesic "28. 10. – 3. 11. 2026" / "28 Oct – 3 Nov 2026"
// s longMonth v anglictine cely nazev mesice ("28 October – 3 November 2026")
// (Intl.formatRange v cestine dava "20.10.2026 – 26.10.2026", proto se rozsah sklada rucne)
export function getDateRangeTitle(from: Date, to: Date, loc = getLocale(), longMonth = false): string {
	const month = getMonthFormat(loc, longMonth);
	const toTitle = new Intl.DateTimeFormat(loc, { day: "numeric", month, year: "numeric" }).format(to);
	// u zacatku rozsahu jen to, cim se lisi od konce (stejny mesic = jen den)
	const fromOptions: Intl.DateTimeFormatOptions = { day: "numeric" };

	if (from.getFullYear() === to.getFullYear()) {
		if (from.getMonth() !== to.getMonth()) {
			fromOptions.month = month;
		}
	} else {
		fromOptions.month = month;
		fromOptions.year = "numeric";
	}

	const fromTitle = new Intl.DateTimeFormat(loc, fromOptions).format(from);

	return `${fromTitle} – ${toTitle}`;
}

export function getWeekDays(loc = getLocale()): Array<string> {
	const days: string[] = [];

	for (let ind = 0; ind < DATE_CONST.daysPerWeek; ind++) {
		days.push(getDayOfWeek(new Date(DATE_CONST.mondayYear, DATE_CONST.mondayMonth, DATE_CONST.mondayDay + ind), loc));
	}

	return days;
}

export function compareDates(dateOne: Date, dateTwo: Date): boolean {
	return dateOne.getFullYear() === dateTwo.getFullYear() && dateOne.getMonth() === dateTwo.getMonth() && dateOne.getDate() === dateTwo.getDate();
}

function numberPadZeroLen2(value: number) {
	return value.toString().padStart(2, "0");
}

export function dateToBe(date: Date): string {
	return `${date.getFullYear()}-${numberPadZeroLen2(date.getMonth() + 1)}-${numberPadZeroLen2(date.getDate())}`;
}

export function fullDateToBe(date: Date): string {
	return `${date.getFullYear()}-${numberPadZeroLen2(date.getMonth() + 1)}-${numberPadZeroLen2(date.getDate())} ${numberPadZeroLen2(date.getHours())}:${numberPadZeroLen2(date.getMinutes())}:${numberPadZeroLen2(date.getSeconds())}`;
}

export function parseCzechDate(dateStr: string): null | Date {
	const dateMatch = dateStr.match(/(\d+)\. (\d+)\. (\d+)/u);

	if (dateMatch) {
		return new Date(parseFloat(dateMatch[3]), parseFloat(dateMatch[2]) - 1, parseFloat(dateMatch[1]));
	}

	return null;
}

export function getDaysBetweenDatesBe(beFrom: string, beTo: string): number {
	const fromDate = new Date(beFrom);
	const toDate = new Date(beTo);
	const diff = toDate.getTime() - fromDate.getTime();
	const days = Math.floor(diff / (1000 * 24 * 3600));

	return days + 1;
}

export function getWeeks(beDate: string, daysData: Array<ICalendarDayData> = []) {
	const weeks: IWeeks = [];
	const curDateBe = dateToBe(new Date());
	const date = parseBeDate(beDate);
	const firstDay = getFirstDay(date);
	const maxDate = daysInMonth(date);
	const month = date.getMonth();
	const year = date.getFullYear();
	let wasStart = false;
	let dayInd = 1;

	while (true) {
		const weekDays: Array<IDayItem> = [];

		for (let day = 0; day < DATE_CONST.daysPerWeek; day++) {
			const compCurDate = new Date(year, month, dayInd);
			const dayBeDate = dateToBe(compCurDate);
			const filtered = daysData.filter(item => item.beDate === dayBeDate);
			const dayItem = {
				beDate: dayBeDate,
				title: "",
				curDay: dayBeDate === curDateBe,
				...filtered.length === 1
					? filtered[0]
					: {},
			} as IDayItem;

			if (!wasStart && day === firstDay) {
				wasStart = true;
			}

			if (wasStart && dayInd <= maxDate) {
				dayItem.title = dayInd.toString();
				dayInd++;
			}

			weekDays.push(dayItem);
		}

		weeks.push(weekDays);

		if (dayInd > maxDate) {
			break;
		}
	}

	return weeks;
}

// posun data o pocet dni (i zaporny), 2026-10-28 + 7 -> 2026-11-04
export function addDaysBe(beDate: string, days: number): string {
	const date = parseBeDate(beDate);

	date.setDate(date.getDate() + days);

	return dateToBe(date);
}

// pondeli tydne, do ktereho datum patri
export function getMondayBe(beDate: string): string {
	// getDay(): 0 = nedele, 1 = pondeli...
	const dayFromMonday = (parseBeDate(beDate).getDay() + 6) % 7;

	return addDaysBe(beDate, -dayFromMonday);
}

export function getBeDates(startDate: string, endDate: string): Array<string> {
	const output: Array<string> = [];
	const date1 = parseBeDate(startDate);
	const date2 = parseBeDate(endDate);
	let fromDate = date1;
	let toDate = date2;

	if (date1.getTime() > date2.getTime()) {
		fromDate = date2;
		toDate = date1;
	}

	while (fromDate.getTime() <= toDate.getTime()) {
		output.push(dateToBe(fromDate));
		fromDate.setDate(fromDate.getDate() + 1);
	}

	return output;
}
